import { SelectionModel } from '@angular/cdk/collections';
import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnInit, Output, SimpleChanges, TemplateRef, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatPaginator, MatPaginatorModule } from '@angular/material/paginator';
import { MatSort, MatSortModule, Sort } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';


@Component({
  selector: 'mce-data-table',
  imports: [CommonModule, MatFormFieldModule, MatInputModule, MatTableModule, MatPaginatorModule, MatSortModule, MatIconModule, MatCheckboxModule],
  templateUrl: './mce-data-table.component.html',
  styleUrls: ['./mce-data-table.component.scss']
})
export class MceDataTableComponent implements OnInit {
  @Input() displayedColumns: string[] = [];
  @Input() data: any[] = [];
  @Input() headers: { [key: string]: string } = {};
  @Input() cellTemplates: { [key: string]: TemplateRef<any> } = {};
  @Input() showSelect: boolean = false;

  @Output() rowsSelected = new EventEmitter<any[]>();
  @Output() sortChange = new EventEmitter<Sort>();

  readonly selection = new SelectionModel<any>(true, []);

  @ViewChild(MatSort) sort?: MatSort;
  @ViewChild(MatPaginator) paginator?: MatPaginator;

  dataSource = new MatTableDataSource<any>([]);
  public finalDisplayedColumns: string[] = [];

  showFilter: boolean = true;
  showPaginator: boolean = true;

  ngOnInit() {
    this.setFinalDisplayedColumns();
  }

  setFinalDisplayedColumns() {
    if (this.showSelect) {
      this.finalDisplayedColumns = ['select', ...this.displayedColumns];
    } else {
      this.finalDisplayedColumns = [...this.displayedColumns];
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.displayedColumns) {
      this.setFinalDisplayedColumns();
    }

    if (changes.data) {
      this.dataSource = new MatTableDataSource(this.data ?? []);
      this.dataSource.filterPredicate = (data, filter) =>
        this.customFilterPredicate(data, filter);

      if (this.sort) {
        this.dataSource.sort = this.sort;
      }

      if (this.paginator) {
        this.dataSource.paginator = this.paginator;
      }

      this.showFilter = !!this.data && this.data.length > 10;
      this.showPaginator = !!this.data && this.data.length > 10;

      this.syncSelectionToCurrentData();
    }
  }

  ngAfterViewInit() {
    if (this.dataSource) {
      this.dataSource.sort = this.sort;
      this.dataSource.paginator = this.paginator;
    }
  }

  private syncSelectionToCurrentData() {
    const currentData = this.data ?? [];
    const validSelection = this.selection.selected.filter(row => currentData.includes(row));

    this.selection.clear();

    if (validSelection.length) {
      this.selection.select(...validSelection);
    }

    this.rowsSelected.emit([...this.selection.selected]);
  }

  isAllSelected() {
    return this.selection.selected.length === (this.data?.length ?? 0);
  }

  isSelected(row: any): boolean {
    return this.selection.isSelected(row);
  }

  toggleRow(row: any) {
    this.selection.toggle(row);
    this.rowsSelected.emit([...this.selection.selected]);
  }

  masterToggle() {
    if (this.isAllSelected()) {
      this.selection.clear();
    } else {
      this.selection.clear();
      this.selection.select(...(this.data ?? []));
    }

    this.rowsSelected.emit([...this.selection.selected]);
  }

  applyFilter(event: Event) {
    const filterValue = (event.target as HTMLInputElement).value;
    this.dataSource.filter = filterValue.trim();
  }

  customFilterPredicate(data: any, filter: string): boolean {
    const terms = filter.split(/\s+/).filter(f => f.trim().length > 0);

    const generalTerms = terms.filter(term => !term.startsWith('+') && !term.startsWith('-'));
    const includeTerms = terms.filter(term => term.startsWith('+')).map(term => term.substring(1));
    const excludeTerms = terms.filter(term => term.startsWith('-')).map(term => term.substring(1));

    const dataString = JSON.stringify(data).toLowerCase();

    let generalTermMatch = false;
    for (const term of generalTerms) {
      if (dataString.includes(term.toLowerCase())) {
        generalTermMatch = true;
        break;
      }
    }

    if (generalTerms.length > 0 && !generalTermMatch) {
      return false;
    }

    for (const term of includeTerms) {
      if (!dataString.includes(term.toLowerCase())) {
        return false;
      }
    }

    for (const term of excludeTerms) {
      if (dataString.includes(term.toLowerCase())) {
        return false;
      }
    }

    return true;
  }
}
